import React, { useEffect, Fragment, useState, useMemo } from "react";
import CookieConsent from "react-cookie-consent";
import {
  BrowserRouter as Router,
  Link,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { loadAnalytics, page, identify, trackGoogleAdsSignup } from "./analytics.ts";
import { jwtDecode } from "jwt-decode";
import { Routes } from "./Routes";
import { AccessContext, PrivacyModeContextProvider, OnboardingGateProvider, useOnboardingGate, AccountantViewProvider } from "@/contexts";
import { Screen, DesktopSidebar, MobileMenu, MobileNav, GlobalAlert } from "@/components";
import { Logo, TaxYearPicker, MsThemeToggle, Pill, Lab } from "@/components/ms";
import { ShellAlert } from "@/components/ShellAlert";
import { Loading } from "@/components/Loading";
import { axios, setAuthToken, setOnUnauthorized } from "@/axios";
import { ErrorBoundary } from "react-error-boundary";
import { GlobalDebug } from "./utils/remove-console";
import { GlobalErrorHandler } from "@/components/ErrorTracking/GlobalErrorHandler";
import { trackEvent } from "@/analytics";
import { CONVERSION_EVENTS, ERROR_RESILIENCE_EVENTS } from "@/analytics/events";
import queryString from "query-string";
import { useAttributionTracking } from "@/hooks/useAttributionTracking";
import { AttributionTracker } from "@/utils/attribution";

import { OnboardingActorProvider } from "@moonscape/shared";
import { useTheme } from "@/hooks/useTheme";
import { TaxYearProvider } from "@/screens/TaxScreen/contexts";

const EMAIL_CLAIM = "https://moonscape.app/email";
const LOGINS_COUNT_CLAIM = "https://moonscape.app/loginsCount";
const EMAIL_VERIFIED_CLAIM = "https://moonscape.app/emailVerified";

/** ErrorBoundaryFallback component  */
function ErrorFallback({ error, resetErrorBoundary }) {
  // Track critical error
  React.useEffect(() => {
    try {
      trackEvent(ERROR_RESILIENCE_EVENTS.CRITICAL_ERROR_OCCURRED, {
        error_message: error.message,
        error_type: error.name || "error_boundary",
        error_code: "ERROR_BOUNDARY",
        error_stack: error.stack?.substring(0, 500),
        url: window.location.href,
        source: "web",
      });
    } catch (trackingError) {
      console.error("[ErrorFallback] Error tracking critical error:", trackingError);
    }
  }, [error]);

  const copyError = () => {
    const errorInfo = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    navigator.clipboard.writeText(JSON.stringify(errorInfo, null, 2));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ms-bg p-[var(--pad)] text-ms-ink">
      <div className="w-full max-w-2xl">
        <Lab>Something broke</Lab>
        <h1 className="mt-2.5 text-[clamp(24px,3.4vw,30px)] font-semibold leading-[1.14] tracking-[-0.032em]">
          Houston, we have a problem.
        </h1>
        <p className="mt-2.5 max-w-[52em] text-[13.5px] leading-[1.55] text-ms-mute">
          Your data is safe — this is the page failing to draw, not the numbers behind it.
        </p>

        <div className="mt-[var(--gap)] flex flex-col">
          <div className="flex items-baseline justify-between gap-3 border-b border-ms-ink pb-[7px]">
            <Lab>What happened</Lab>
          </div>
          <div className="border-t border-ms-line py-[11px] text-[13.5px]">
            {error.message || "An unexpected error occurred"}
          </div>
          <pre className="max-h-64 overflow-auto border border-ms-line bg-ms-card2 p-3.5 font-mono text-[11px] leading-[1.5] text-ms-mute">
            {error.stack || "No stack trace available"}
          </pre>
        </div>

        <div className="mt-[var(--gap)] flex flex-col gap-2.5 sm:flex-row">
          <Pill onClick={resetErrorBoundary}>Try again</Pill>
          <Pill variant="ghost" onClick={copyError}>
            Copy error
          </Pill>
          <Pill variant="ghost" onClick={() => window.location.reload()}>
            Reload
          </Pill>
        </div>

        <div className="mt-[var(--gap)] border border-l-[3px] border-ms-line border-l-ms-rule px-[15px] py-3 text-[12px] leading-[1.5] text-ms-mute">
          <b className="font-semibold text-ms-ink">Need help?</b> If this keeps happening, copy the
          error above and send it to support — it tells us exactly where to look.
        </div>
      </div>
    </div>
  );
}

/**
 * Inner layout component that uses onboarding gate to conditionally show sidebar.
 * Sidebar is hidden during forced onboarding (new users completing quiz).
 */
function AppLayout({ isAuthenticated, user, mobileMenuOpen, setMobileMenuOpen }) {
  const { isInForcedOnboarding } = useOnboardingGate();
  // First run owns the whole viewport for its whole length, not just until the
  // gate flips: saving the jurisdiction completes onboarding halfway through the
  // flow, and without this the sidebar would reappear over the remaining steps.
  const inFirstRun = useLocation().pathname === "/quiz";
  // the whole chrome — rail, bottom nav, header — goes away during forced onboarding
  const showNav = isAuthenticated && !isInForcedOnboarding && !inFirstRun;
  const showHeader = isAuthenticated && !isInForcedOnboarding && !inFirstRun;

  return (
    <AccountantViewProvider>
      <TaxYearProvider>
        <div
          className={
            showNav ? "ms-shell bg-ms-bg text-ms-ink" : "min-h-screen bg-ms-bg text-ms-ink"
          }
        >
          {showNav && <DesktopSidebar />}

          {showHeader && (
            <header className="ms-a-head sticky top-0 z-20 flex min-h-[52px] items-center justify-between gap-3 border-b border-ms-rule bg-ms-bg px-[var(--pad)] print:hidden">
              <Link to="/" className="flex min-w-0 flex-1 items-center gap-2.5 ms1100:invisible">
                <Logo width={18} />
              </Link>
              <span className="flex flex-none items-center gap-2.5">
                <TaxYearPicker />
                <MsThemeToggle />
              </span>
            </header>
          )}

          <main className="ms-a-main flex min-w-0 flex-col">
            {showHeader && user && (
              <div className="print:hidden">
                <ShellAlert userId={user.sub} />
              </div>
            )}
            {/* this has all the different routes on the sidebar */}
            <Routes />
          </main>

          {showNav && <MobileNav setMobileMenuOpen={setMobileMenuOpen} />}
          {showNav && (
            <MobileMenu
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          )}
        </div>
      </TaxYearProvider>
    </AccountantViewProvider>
  );
}

export default function App() {
  // Initialize theme system - MUST be at the top so it runs before render
  useTheme();

  /**
   * @REMOVE_CONSOLES
   * // remove the working of console logs
   * // remove any accidental use of console logs
   */
  useEffect(() => {
    process.env.NODE_ENV === "production" && GlobalDebug(false);
  }, []);

  // Initialize destinations before attribution emits its first event.
  useEffect(() => {
    loadAnalytics();
    AttributionTracker.init();
  }, []);

  // Track attribution when user authenticates
  useAttributionTracking();

  const [accessToken, setAccessToken] = useState(null);
  const [loginsCount, setLoginsCount] = useState(null);
  const [emailVerified, setEmailVerified] = useState(null);

  const {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  // @ts-ignore
  // If under test in Cypress, get credentials from "auth0Cypress" localstorage item and send event to our state management to log the user into the SPA
  // if (window.Cypress) {
  //   // eslint-disable-next-line react-hooks/rules-of-hooks
  //   useEffect(() => {
  //     const auth0 = JSON.parse(localStorage.getItem("auth0Cypress"));
  //     console.log("[App] auth0", auth0);
  //     // authService.send("AUTH0", {
  //     //   user: auth0.body.decodedToken.user,
  //     //   token: auth0.body.access_token,
  //     // });
  //     setAccessToken(auth0.body.access_token);
  //     setAxiosTokenInterceptor(auth0.body.access_token);
  //   }, [accessToken]);
  // } else {

  // Set up 401 handler to redirect to login
  useEffect(() => {
    setOnUnauthorized(() => {
      console.log("[App] 401 received - redirecting to login");
      trackEvent("login redirect from 401", {
        action: "login redirect",
        category: "auth",
        label: "401 unauthorized",
      });
      loginWithRedirect();
    });
  }, [loginWithRedirect]);

  // track clicks and interactions
  const history = useHistory();

  useEffect(() => {
    const handleDocumentClick = (event) => {
      const { target } = event;
      // if (target.tagName === "A" || target.tagName === "BUTTON") {
      if (target.tagName === "BUTTON") {
        // Call your tracking function here
        // console.log(`[analytics] Tracking: ${target.tagName} click`);
        // If it's a link, you might want to prevent the default navigation
        // if (target.tagName === "A") {
        //   event.preventDefault();
        //   // Programmatically navigate if using react-router
        //   navigate(target.getAttribute("href"));
        // history.push("/home");
        // }
      }
    };

    // Attach the event listener to the document
    document.addEventListener("click", handleDocumentClick);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [history]);

  const location = useLocation();

  // Auth0 initial access token exchange
  useEffect(() => {
    async function getAccessToken() {
      try {
        // get the access token
        const accessToken = await getAccessTokenSilently();

        // console.log("[user] accessToken ", accessToken);

        const decodedToken = jwtDecode(accessToken);

        console.log("[user] decodedToken ", decodedToken);

        // Now you can access your custom claims
        const loginsCount = decodedToken[LOGINS_COUNT_CLAIM];
        const emailVerified = decodedToken[EMAIL_VERIFIED_CLAIM];

        console.log("[user] loginsCount ", loginsCount);

        setEmailVerified(emailVerified);
        setLoginsCount(loginsCount);

        // trigger google conversion for signup
        if (loginsCount === 1) {
          trackGoogleAdsSignup(loginsCount, user?.sub);

          const eventId = trackEvent(CONVERSION_EVENTS.SIGN_UP, {
            category: "signup",
            action: "complete_registration",
            source: "web",
            method: "auth0",
          });

          // track signup conversion
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "signup_account_creation",
            event_id: eventId,
          });

          console.log("[user] signup conversion");
        }

        // check if email is verified

        if (!emailVerified) {
          history.push("/verify-email");
        }

        setAuthToken(accessToken);
        setAccessToken(accessToken);
        console.log("[analytics] getAccessTokenSilently resp");
      } catch (e) {
        console.log("[analytics] error getting access token ", e);
        // If the error is due to an expired token, redirect the user to the login page
        if (e.error === "login_required" || e.error === "invalid_token") {
          // TODO: track user login required
          trackEvent("login redirect from dashboard /", {
            action: "login redirect",
            category: "login",
            label: "login redirect",
            value: 1,
          });
          console.log("[analytics] login redirect via dashboard /");

          // Navigate to the link after a delay to ensure the event is tracked
          setTimeout(() => {
            loginWithRedirect();
          }, 250);
        }
      }
    }

    const values = queryString.parse(location.search);
    const hasError = !!values.error;
    const redirectParam = values.redirect;

    console.log("[verify] values", values);

    if (!hasError && !isAuthenticated && !isLoading) {
      console.log("[analytics] not authenticated, redirecting to login");
      trackEvent("login redirect from dashboard /", {
        action: "login redirect",
        category: "login",
        label: "login redirect",
        value: 1,
      });
      console.log("[analytics] login redirect via dashboard /");
      // Navigate to the link after a delay to ensure the event is tracked
      setTimeout(() => {
        loginWithRedirect({
          appState: redirectParam ? { targetUrl: redirectParam } : undefined
        });
      }, 250);
      return;
    }
    if (!hasError && isAuthenticated) {
      // If user is authenticated and has a redirect param, navigate to it
      if (redirectParam) {
        console.log("[auth] redirecting authenticated user to:", redirectParam);
        history.push(redirectParam);
        return;
      }

      // identify user for segment
      identify(user?.email);
      console.log("[analytics] getting access token ", user?.email);
      getAccessToken();
    }
  }, [
    getAccessTokenSilently,
    history,
    isAuthenticated,
    isLoading,
    location.search,
    loginWithRedirect,
    user,
  ]);

  const [isFooterVisible, setIsFooterVisible] = useState(true);

  const [isMobile, setIsMobile] = useState(false);
  const [hasTrackedMobileWarning, setHasTrackedMobileWarning] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      if (isMobile && !hasTrackedMobileWarning) {
        trackEvent("mobile takeover - optimized for desktop", {});
        setHasTrackedMobileWarning(true);
      }
    };

    checkMobile();
  }, [hasTrackedMobileWarning, isMobile]);

  // access context stores the auth0 access token
  const value = useMemo(() => {
    // console.log("[App] useMemo accessToken", accessToken)
    return accessToken;
  }, [accessToken]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <Router>
      <GlobalErrorHandler />
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => {
          // reset the state of your app so the error doesn't happen again
        }}
      >
        <GlobalAlert />
        <OnboardingActorProvider>
          <AccessContext.Provider value={value}>
            {/* WebSocketProvider is mounted once, in index.js, above this tree.
                A second one here made two React contexts over one singleton
                socket, and the unsubscribe path disconnects that socket whenever
                the subscriber count reaches zero — so an unmount ordering that
                briefly hit zero closed it for every consumer. GlobalAlert above
                already reads the outer provider, so the outer is the one to keep. */}
              <PrivacyModeContextProvider>
                <OnboardingGateProvider>
                  <AppLayout
                    isAuthenticated={isAuthenticated}
                    user={user}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                  />
                </OnboardingGateProvider>

              {false && (
                <footer className="mobile-footer text-sm fixed inset-x-0 bottom-0 bg-gray-800 text-white text-center px-4 pt-3 md:hidden">
                  <p className="">
                    This website is optimized for desktop. If you're interested
                    in a mobile version, please let us know by clicking the
                    button
                  </p>
                  <a
                    href="mailto:example@example.com?subject=Mobile%20Version%20Interest&body=I'm%20interested%20in%20a%20mobile%20version%20of%20the%20website.%20Here%20are%20my%20suggestions:"
                    className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                  >
                    <button
                      type="button"
                      className=" mx-4 mb-4 rounded-full mt-6 bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                    >
                      I want a mobile version 📱
                    </button>
                  </a>
                </footer>
              )}
              <CookieConsent
                location="bottom"
                buttonText="OK, got it!"
                cookieName="moonscape_cookie_consent"
                style={{
                  background: "var(--ms-card)",
                  color: "var(--ms-mute)",
                  borderTop: "3px solid var(--ms-rule)",
                  fontSize: "12px",
                  lineHeight: 1.5,
                  alignItems: "center",
                  paddingRight: 128,
                }}
                buttonStyle={{
                  background: "var(--ms-pill-bg)",
                  color: "var(--ms-pill-ink)",
                  borderRadius: 999,
                  minHeight: 40,
                  padding: "0 20px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
                // expires={150}
              >
                We use cookies to ensure you get the best experience on our
                website.
                <span style={{ fontSize: "10px" }}>Learn more</span>
              </CookieConsent>
            </PrivacyModeContextProvider>
        </AccessContext.Provider>
      </OnboardingActorProvider>
      </ErrorBoundary>
    </Router>
  );
}
